import { useState, useEffect, Fragment } from 'react';
import Papa from 'papaparse';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { determinarObligaciones, calcularMuestraGIII } from '../lib/scoring';
import { GUIA_I_PREGUNTAS, GUIA_II_PREGUNTAS, GUIA_III_PREGUNTAS } from '../data';
import { Plus, Upload, Download, Users, Send, Copy, Check, Clock, RefreshCw, CheckCircle, AlertCircle, Mail, Link2, Pencil, Trash2, X } from 'lucide-react';
import * as api from '../lib/api';
import UpgradeModal from '../components/UpgradeModal';

const STATUS_CONFIG = {
  pendiente: { bg: '#fef3c7', color: '#92400e', icon: Clock, label: 'Pendiente' },
  en_progreso: { bg: '#dbeafe', color: '#1e40af', icon: RefreshCw, label: 'En progreso' },
  completado: { bg: '#dcfce7', color: '#166534', icon: CheckCircle, label: 'Completado' },
  rechazado: { bg: '#fee2e2', color: '#991b1b', icon: AlertCircle, label: 'Rechazado' },
};

export default function TrabajadoresPage() {
  const { state, dispatch } = useAppState();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  if (!centro) return null;

  const trabajadores = centro.trabajadores || [];
  const cuestionarios = centro.cuestionariosCompletos || {};
  const fichas = centro.fichas || {};
  const consentimientos = centro.consentimientos || {};
  const obligaciones = determinarObligaciones(centro.numTrabajadores);

  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ numEmpleado: '', nombre: '', apellidoPaterno: '', apellidoMaterno: '', metodoInvitacion: '', contacto: '' });
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('todos');
  const [limitError, setLimitError] = useState(null);

  // Portal state
  const [portalTokens, setPortalTokens] = useState({});  // tid → token data
  const [generating, setGenerating] = useState(null);     // tid being generated
  const [copied, setCopied] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [showBulkResult, setShowBulkResult] = useState(null);

  // Edit/delete state
  const [editingTrab, setEditingTrab] = useState(null);  // trab object being edited
  const [editForm, setEditForm] = useState({});
  const [deleting, setDeleting] = useState(null);  // tid being deleted

  // Muestreo GIII (centros 51+)
  const [estrategiaMuestreo, setEstrategiaMuestreo] = useState(() =>
    Array.isArray(state.muestraSeleccionada[centro?.id]) ? 'muestra' : 'todos'
  );
  const nMuestra = obligaciones.nivel >= 3 ? calcularMuestraGIII(centro.numTrabajadores) : null;

  // Sync estrategia when centro changes
  useEffect(() => {
    setEstrategiaMuestreo(Array.isArray(state.muestraSeleccionada[centro?.id]) ? 'muestra' : 'todos');
  }, [centro?.id]);

  const baseUrl = window.location.origin;

  // Load portal tokens for this centro
  useEffect(() => {
    if (!state.empresaId) return;
    api.getTokens(state.empresaId).then(tokens => {
      const map = {};
      // Keep most recent token per worker in this centro
      for (const tk of tokens) {
        if (tk.centro_id !== centro.id) continue;
        const existing = map[tk.trabajador_id];
        if (!existing || (tk.created_at || '') > (existing.created_at || '')) {
          map[tk.trabajador_id] = tk;
        }
      }
      setPortalTokens(map);
    }).catch(() => {});
  }, [state.empresaId, centro.id]);

  // Filter workers
  const trabajadoresFiltrados = trabajadores.filter(tw => {
    const texto = `${tw.nombreCompleto || ''} ${tw.nombre || ''} ${tw.apellidoPaterno || ''} ${tw.numEmpleado || ''} ${tw.area || ''} ${tw.puesto || ''} ${(fichas[tw.id]?.area) || ''} ${(fichas[tw.id]?.puesto) || ''}`.toLowerCase();
    if (busqueda && !texto.includes(busqueda.toLowerCase())) return false;
    if (filtroEstatus === 'completos') {
      const g1 = !!cuestionarios[`${tw.id}_guiaI`]; const fichaOk = !!fichas[tw.id]; const consentOk = !!consentimientos[tw.id];
      return g1 && fichaOk && consentOk;
    }
    if (filtroEstatus === 'pendientes') {
      const g1 = !!cuestionarios[`${tw.id}_guiaI`]; const fichaOk = !!fichas[tw.id]; const consentOk = !!consentimientos[tw.id];
      return !g1 || !fichaOk || !consentOk;
    }
    if (filtroEstatus === 'sin_link') return !portalTokens[tw.id];
    if (filtroEstatus === 'con_link') return !!portalTokens[tw.id];
    return true;
  });

  const thStyle = { padding: '10px 12px', fontSize: '0.68rem', fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: `2px solid ${t.border}` };
  const inputStyle = { padding: '10px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? 'rgba(255,255,255,0.04)' : '#fff', color: t.textStrong, fontSize: '0.85rem' };
  const labelStyle = { fontSize: '0.72rem', fontWeight: 600, color: t.textMuted, marginBottom: 4, display: 'block' };

  // ── Worker add ──────────────────────────────────────────
  const agregarTrabajador = async () => {
    if (!form.nombre.trim() || !form.apellidoPaterno.trim() || !form.numEmpleado.trim()) return;
    setLimitError(null);
    const numEmpleado = form.numEmpleado.trim();
    if (trabajadores.some(t => t.numEmpleado === numEmpleado)) {
      alert(`Ya existe un trabajador con el número de empleado "${numEmpleado}".`);
      return;
    }
    const nombreCompleto = `${form.nombre.trim()} ${form.apellidoPaterno.trim()}${form.apellidoMaterno.trim() ? ' ' + form.apellidoMaterno.trim() : ''}`;
    const trabajador = { id: `t_${Date.now()}`, numEmpleado: form.numEmpleado.trim(), nombre: form.nombre.trim(), apellidoPaterno: form.apellidoPaterno.trim(), apellidoMaterno: form.apellidoMaterno.trim(), nombreCompleto, metodoInvitacion: form.metodoInvitacion || 'No asignado', contacto: form.contacto.trim(), area: 'Sin asignar', puesto: 'Sin asignar' };
    try {
      await api.addTrabajador(state.empresaId, centro.id, trabajador);
      dispatch({ type: ACTION_TYPES.ADD_TRABAJADOR, payload: { centroIdx: state.centroActualIdx, trabajador } });
      setForm({ numEmpleado: '', nombre: '', apellidoPaterno: '', apellidoMaterno: '', metodoInvitacion: '', contacto: '' });
    } catch (err) {
      if (err.code === 'TRABAJADOR_LIMIT') { setLimitError(err.detail); }
      else { alert('Error: ' + err.message); }
    }
  };

  // ── CSV import ──────────────────────────────────────────
  const descargarPlantilla = () => {
    const csv = "No. Empleado,Nombre(s),Apellido Paterno,Apellido Materno,Método de Invitación,Dato de Contacto\n001,Juan Carlos,Pérez,López,Correo electrónico,juan.perez@empresa.com\n002,María,García,Ruiz,WhatsApp,52 1 55 9876 5432";
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'plantilla_nom035.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Neutraliza inyección de fórmulas (CSV injection): si un valor empieza con
  // =, +, -, @ o tab, Excel/Sheets podría interpretarlo como fórmula al abrir
  // un CSV exportado más adelante — se antepone un apóstrofe para forzar texto plano.
  const sanitizeCSVField = (v) => /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;

  const procesarCSV = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const result = Papa.parse(e.target.result, { skipEmptyLines: true });
        const rows = result.data;
        if (!rows || rows.length < 2) { setImportResult({ success: false, error: 'Archivo vacío o sin datos.' }); return; }
        let headerIdx = 0;
        for (let i = 0; i < Math.min(rows.length, 10); i++) {
          if (rows[i]?.[0] && String(rows[i][0]).trim().toLowerCase().includes('no')) { headerIdx = i; break; }
        }
        const nuevos = [], errores = [];
        const existingNums = new Set(trabajadores.map(t => t.numEmpleado));
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.every(c => !c || !String(c).trim())) continue;
          const [num, nom, ap, am, met, con] = row.map(c => sanitizeCSVField(String(c || '').trim()));
          if (!num || !nom || !ap) { errores.push(`Fila ${i+1}: Faltan campos obligatorios`); continue; }
          if (existingNums.has(num)) { errores.push(`Fila ${i+1}: No. '${num}' ya existe`); continue; }
          const nombreCompleto = `${nom} ${ap}${am ? ' ' + am : ''}`;
          nuevos.push({ id: `t_${Date.now()}_${i}`, numEmpleado: num, nombre: nom, apellidoPaterno: ap, apellidoMaterno: am, nombreCompleto, metodoInvitacion: met || 'No asignado', contacto: con, area: 'Sin asignar', puesto: 'Sin asignar' });
          existingNums.add(num);
        }
        if (nuevos.length > 0) {
          try {
            await api.importTrabajadores(state.empresaId, centro.id, nuevos);
            dispatch({ type: ACTION_TYPES.IMPORT_TRABAJADORES, payload: { centroIdx: state.centroActualIdx, trabajadores: nuevos } });
            setImportResult({ success: true, importados: nuevos.length, errores });
          } catch (err) {
            if (err.code === 'TRABAJADOR_LIMIT') { setLimitError(err.detail); setImportResult(null); }
            else { setImportResult({ success: false, error: err.message }); }
          }
        } else {
          setImportResult({ success: true, importados: 0, errores });
        }
      } catch (err) { setImportResult({ success: false, error: err.message }); }
    };
    reader.readAsText(file);
  };

  // ── Portal: generate link for one worker ────────────────
  const generateLink = async (tid) => {
    setGenerating(tid);
    try {
      const res = await api.generarTokens(state.empresaId, centro.id, [tid]);
      if (res.tokens?.length > 0) {
        const tk = res.tokens[0];
        setPortalTokens(prev => ({ ...prev, [tid]: { ...tk, status: 'pendiente' } }));
        // Auto-copy
        navigator.clipboard.writeText(`${baseUrl}/portal/${tk.token}`);
        setCopied(tid);
        setTimeout(() => setCopied(null), 2500);
      }
    } catch (err) { alert('Error generando link: ' + err.message); }
    setGenerating(null);
  };

  // ── Portal: bulk generate ───────────────────────────────
  const handleBulkGenerate = async () => {
    if (bulkSelected.size === 0) return;
    setBulkGenerating(true);
    try {
      const res = await api.generarTokens(state.empresaId, centro.id, Array.from(bulkSelected));
      const newMap = { ...portalTokens };
      for (const tk of res.tokens || []) {
        newMap[tk.trabajador_id] = { ...tk, status: 'pendiente' };
      }
      setPortalTokens(newMap);
      setShowBulkResult(res.tokens || []);
      setBulkSelected(new Set());
    } catch (err) { alert('Error: ' + err.message); }
    setBulkGenerating(false);
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${baseUrl}/portal/${token}`);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  const mailtoLink = (token, nombre, contacto) => {
    const url = `${baseUrl}/portal/${token}`;
    const subject = encodeURIComponent('Evaluación NOM-035 — Tu participación');
    const body = encodeURIComponent(`Hola ${nombre.split(' ')[0]},\n\nTe invitamos a completar la evaluación de riesgos psicosociales NOM-035.\n\nAccede aquí: ${url}\n\nEl link expira en 30 días.\n\nGracias.`);
    return `mailto:${contacto}?subject=${subject}&body=${body}`;
  };

  // ── Edit/Delete handlers ─────────────────────────────────
  const startEdit = (trab) => {
    setEditingTrab(trab);
    setEditForm({
      numEmpleado: trab.numEmpleado || '', nombre: trab.nombre || '', apellidoPaterno: trab.apellidoPaterno || '',
      apellidoMaterno: trab.apellidoMaterno || '', metodoInvitacion: trab.metodoInvitacion || '', contacto: trab.contacto || '',
    });
  };

  const saveEdit = async () => {
    if (!editingTrab) return;
    const numEmpleado = editForm.numEmpleado.trim();
    if (trabajadores.some(t => t.id !== editingTrab.id && t.numEmpleado === numEmpleado)) {
      alert(`Ya existe otro trabajador con el número de empleado "${numEmpleado}".`);
      return;
    }
    const nombreCompleto = `${editForm.nombre.trim()} ${editForm.apellidoPaterno.trim()}${editForm.apellidoMaterno.trim() ? ' ' + editForm.apellidoMaterno.trim() : ''}`;
    const updated = { ...editingTrab, ...editForm, nombreCompleto };
    try {
      await api.updateTrabajador(state.empresaId, centro.id, editingTrab.id, updated);
      dispatch({ type: ACTION_TYPES.UPDATE_TRABAJADOR, payload: { centroIdx: state.centroActualIdx, trabajadorId: editingTrab.id, data: updated } });
      setEditingTrab(null);
    } catch (err) { alert('Error: ' + err.message); }
  };

  const handleDelete = async (trab) => {
    if (!confirm(`¿Eliminar a ${trab.nombreCompleto}? Esta acción no se puede deshacer.`)) return;
    setDeleting(trab.id);
    try {
      await api.deleteTrabajador(state.empresaId, centro.id, trab.id);
      dispatch({ type: ACTION_TYPES.DELETE_TRABAJADOR, payload: { centroIdx: state.centroActualIdx, trabajadorId: trab.id } });
    } catch (err) { alert('Error: ' + err.message); }
    setDeleting(null);
  };

  // ── Dots and expanded content ───────────────────────────
  const Dot = ({ ok, tab, tid }) => {
    const active = expanded?.trabajadorId === tid && expanded?.tab === tab;
    return <span onClick={e => { e.stopPropagation(); if (ok) setExpanded(active ? null : { trabajadorId: tid, tab }); }}
      style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: ok ? '#10b981' : (dark ? 'rgba(255,255,255,0.1)' : '#d1d5db'), cursor: ok ? 'pointer' : 'default', border: active ? '2px solid #6366f1' : '2px solid transparent', boxSizing: 'content-box' }} />;
  };

  const likertLabels = ['Siempre', 'Casi siempre', 'Algunas veces', 'Casi nunca', 'Nunca'];

  const ExpandedContent = ({ trab }) => {
    const g1raw = cuestionarios[`${trab.id}_guiaI`];
    const g1 = g1raw?.respuestas || g1raw;
    const g2raw = cuestionarios[`${trab.id}_guiaII`];
    const g2 = g2raw?.respuestas || g2raw;
    const g3raw = cuestionarios[`${trab.id}_guiaIII`];
    const g3 = g3raw?.respuestas || g3raw;
    const ficha = fichas[trab.id];
    const consent = consentimientos[trab.id];
    const tab = expanded?.tab;
    const tabs = [g1 && { key: 'I', label: 'Guía I' }, g2 && { key: 'II', label: 'Guía II' }, g3 && { key: 'III', label: 'Guía III' }, ficha && { key: 'ficha', label: 'Ficha' }, consent && { key: 'consent', label: 'Consent.' }].filter(Boolean);

    const GuiaTable = ({ resp, preguntas, isLikert }) => (
      <div style={{ maxHeight: 350, overflowY: 'auto' }}>
        {isLikert ? preguntas.map(dom => (
          <div key={dom.dominio + dom.preguntas[0].id}>
            <div style={{ padding: '6px 10px', background: dark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.05)', fontSize: '0.72rem', fontWeight: 700, color: t.textLink, marginTop: 4 }}>{dom.dominio} · {dom.categoria}</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}><tbody>
              {dom.preguntas.map(p => <tr key={p.id} style={{ borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.03)' : '#f3f4f6'}` }}>
                <td style={{ padding: '4px 10px', color: t.textFaint, width: 30 }}>{p.num}</td>
                <td style={{ padding: '4px 10px', color: t.textStrong }}>{p.texto}</td>
                <td style={{ padding: '4px 10px', textAlign: 'center', width: 110, fontWeight: 600, fontSize: '0.72rem', color: resp[p.id] >= 3 ? '#ef4444' : resp[p.id] >= 2 ? '#f59e0b' : '#10b981' }}>{resp[p.id] !== undefined ? likertLabels[resp[p.id]] || resp[p.id] : '—'}</td>
              </tr>)}
            </tbody></table>
          </div>
        )) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead><tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#f3f4f6' }}>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: t.textMuted, fontWeight: 600, fontSize: '0.7rem' }}>#</th>
              <th style={{ padding: '6px 10px', textAlign: 'left', color: t.textMuted, fontWeight: 600, fontSize: '0.7rem' }}>Pregunta</th>
              <th style={{ padding: '6px 10px', textAlign: 'center', color: t.textMuted, fontWeight: 600, fontSize: '0.7rem', width: 80 }}>Respuesta</th>
            </tr></thead>
            <tbody>{preguntas.filter(p => p.categoria).map((p, i) => <tr key={p.id} style={{ borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.03)' : '#f3f4f6'}` }}>
              <td style={{ padding: '5px 10px', color: t.textFaint }}>{i+1}</td>
              <td style={{ padding: '5px 10px', color: t.textStrong }}>{p.texto}</td>
              <td style={{ padding: '5px 10px', textAlign: 'center', fontWeight: 600, color: resp[p.id] === true ? '#ef4444' : '#10b981' }}>{resp[p.id] === true ? 'Sí' : 'No'}</td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    );

    const FichaView = () => {
      if (!ficha) return null;
      const secs = [
        { t: 'Datos personales', c: [['Sexo', ficha.sexo], ['Edad', ficha.edad], ['Estado civil', ficha.estadoCivil], ['Nivel estudios', ficha.nivelEstudios || ficha.escolaridad]] },
        { t: 'Datos laborales', c: [['Puesto', ficha.puesto], ['Área', ficha.area || ficha.departamento], ['Contratación', ficha.tipoContrato || ficha.tipoContratacion], ['Jornada', ficha.tipoJornada]] },
        { t: 'Experiencia', c: [['Antigüedad', ficha.antiguedad || ficha.tiempoPuesto], ['Experiencia', ficha.experiencia || ficha.tiempoExperiencia]] },
      ];
      const cs = { padding: '8px 12px', background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 6, border: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#f0f0f0'}` };
      return <div>{secs.map(s => <div key={s.t} style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: t.textLink, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${t.border}` }}>{s.t}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {s.c.map(([l, v]) => <div key={l} style={cs}><div style={{ fontSize: '0.65rem', color: t.textFaint, fontWeight: 600, marginBottom: 2 }}>{l}</div><div style={{ fontSize: '0.82rem', color: v ? t.textStrong : t.textFaint }}>{v || '—'}</div></div>)}
        </div>
      </div>)}</div>;
    };

    return (
      <div style={{ padding: '14px 16px', background: dark ? 'rgba(99,102,241,0.04)' : 'rgba(99,102,241,0.02)' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {tabs.map(tb => <button key={tb.key} onClick={e => { e.stopPropagation(); setExpanded({ trabajadorId: trab.id, tab: tb.key }); }}
            style={{ padding: '5px 14px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: tab === tb.key ? t.accent : (dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb'), color: tab === tb.key ? '#fff' : t.textMuted }}>{tb.label}</button>)}
        </div>
        {tab === 'I' && g1 && <GuiaTable resp={g1} preguntas={GUIA_I_PREGUNTAS} isLikert={false} />}
        {tab === 'II' && g2 && <GuiaTable resp={g2} preguntas={GUIA_II_PREGUNTAS} isLikert={true} />}
        {tab === 'III' && g3 && <GuiaTable resp={g3} preguntas={GUIA_III_PREGUNTAS} isLikert={true} />}
        {tab === 'ficha' && <FichaView />}
        {tab === 'consent' && consent && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ padding: '6px 10px', background: 'rgba(16,185,129,0.08)', borderRadius: 6 }}>
              <div style={{ fontSize: '0.65rem', color: t.textFaint, fontWeight: 600 }}>Estado</div>
              <div style={{ fontSize: '0.82rem', color: consent.acepta !== false ? '#10b981' : '#ef4444', fontWeight: 600, marginTop: 2 }}>{consent.acepta !== false ? '✓ Aceptado' : '✗ Rechazado'}</div>
            </div>
            {consent.fecha && <div style={{ padding: '6px 10px', background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 6 }}>
              <div style={{ fontSize: '0.65rem', color: t.textFaint, fontWeight: 600 }}>Fecha</div>
              <div style={{ fontSize: '0.82rem', color: t.textStrong, marginTop: 2 }}>{consent.fecha}</div>
            </div>}
            {consent.metodo && <div style={{ padding: '6px 10px', background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 6 }}>
              <div style={{ fontSize: '0.65rem', color: t.textFaint, fontWeight: 600 }}>Método</div>
              <div style={{ fontSize: '0.82rem', color: t.textStrong, marginTop: 2 }}>{consent.metodo}</div>
            </div>}
            {consent.archivo && <div style={{ padding: '6px 10px', background: dark ? 'rgba(255,255,255,0.02)' : '#f9fafb', borderRadius: 6 }}>
              <div style={{ fontSize: '0.65rem', color: t.textFaint, fontWeight: 600 }}>Archivo firmado</div>
              <div style={{ fontSize: '0.82rem', color: t.textStrong, marginTop: 2 }}>{consent.archivo}{consent.archivoTamano ? ` (${consent.archivoTamano})` : ''}</div>
            </div>}
            {consent.archivoData && (
              <button onClick={() => {
                const mime = consent.archivoTipo || 'application/pdf';
                const blob = Uint8Array.from(atob(consent.archivoData), c => c.charCodeAt(0));
                const url = URL.createObjectURL(new Blob([blob], { type: mime }));
                const a = document.createElement('a'); a.href = url; a.download = consent.archivo || 'consentimiento_firmado'; a.click();
                URL.revokeObjectURL(url);
              }} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${t.border}`, background: 'transparent', color: t.textLink, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                Descargar firmado
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Portal status badge ─────────────────────────────────
  const PortalBadge = ({ tid }) => {
    const tk = portalTokens[tid];
    if (!tk) return <span style={{ fontSize: '0.7rem', color: t.textFaint }}>—</span>;
    const s = STATUS_CONFIG[tk.status] || STATUS_CONFIG.pendiente;
    const Icon = s.icon;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 5, background: s.bg, color: s.color, fontSize: '0.65rem', fontWeight: 600 }}>
        <Icon size={10} /> {s.label}
      </span>
    );
  };

  // ── Portal action button ────────────────────────────────
  const PortalAction = ({ trab }) => {
    const tk = portalTokens[trab.id];
    const isGen = generating === trab.id;
    const isCopied = copied === trab.id || copied === tk?.token;

    if (!tk) {
      // No link yet — show generate button
      return (
        <button onClick={e => { e.stopPropagation(); generateLink(trab.id); }} disabled={isGen}
          style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', borderRadius: 6, border: `1px solid ${t.accent}`, background: 'transparent', color: t.accent, fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', opacity: isGen ? 0.5 : 1, whiteSpace: 'nowrap' }}>
          <Link2 size={10} /> {isGen ? '...' : 'Generar'}
        </button>
      );
    }

    // Has link — show copy + mail
    return (
      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
        <button onClick={() => copyLink(tk.token)} style={{
          display: 'flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 6,
          border: 'none', background: isCopied ? '#10b981' : (dark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'),
          color: isCopied ? '#fff' : t.textMuted, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
        }}>
          {isCopied ? <><Check size={10} /> ✓</> : <><Copy size={10} /></>}
        </button>
        {trab.contacto && trab.contacto.includes('@') && (
          <a href={mailtoLink(tk.token, trab.nombreCompleto, trab.contacto)} onClick={e => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', padding: '4px 8px', borderRadius: 6, border: 'none', background: dark ? 'rgba(255,255,255,0.06)' : '#f1f5f9', color: t.textMuted, fontSize: '0.65rem', textDecoration: 'none' }}>
            <Mail size={10} />
          </a>
        )}
      </div>
    );
  };

  const colSpan = 10 + (obligaciones.guias.includes("II") ? 1 : 0) + (obligaciones.nivel >= 3 ? 1 : 0);
  const pendientes = trabajadores.filter(t => !portalTokens[t.id]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Trabajadores — {centro.nombreCentro}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {pendientes.length > 0 && (
            <button onClick={() => {
              setBulkSelected(new Set(pendientes.map(t => t.id)));
            }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${t.accent}`, background: 'transparent', color: t.accent, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
              <Send size={14} /> Enviar a {pendientes.length} sin link
            </button>
          )}
          <button onClick={() => setShowImport(!showImport)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.8rem', cursor: 'pointer' }}>
            {showImport ? 'Cerrar' : <><Upload size={14} /> Importar CSV</>}
          </button>
        </div>
      </div>

      {/* Bulk generate bar */}
      {bulkSelected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: dark ? 'rgba(99,102,241,0.08)' : '#eef2ff', borderRadius: 10, marginBottom: 16, border: `1px solid ${dark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}` }}>
          <span style={{ fontSize: '0.85rem', color: t.textStrong, fontWeight: 500 }}>{bulkSelected.size} trabajador{bulkSelected.size > 1 ? 'es' : ''} seleccionado{bulkSelected.size > 1 ? 's' : ''}</span>
          <button onClick={handleBulkGenerate} disabled={bulkGenerating} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, border: 'none', background: t.accent, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', marginLeft: 'auto', opacity: bulkGenerating ? 0.7 : 1 }}>
            <Send size={14} /> {bulkGenerating ? 'Generando...' : 'Generar links'}
          </button>
          <button onClick={() => setBulkSelected(new Set())} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.78rem', cursor: 'pointer' }}>Cancelar</button>
        </div>
      )}

      {/* Bulk result */}
      {showBulkResult && (
        <div style={{ padding: '16px 20px', background: dark ? 'rgba(16,185,129,0.08)' : '#ecfdf5', borderRadius: 10, marginBottom: 16, border: `1px solid ${dark ? 'rgba(16,185,129,0.2)' : '#a7f3d0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 600, color: '#166534', fontSize: '0.9rem' }}>✓ {showBulkResult.length} links generados</span>
            <button onClick={() => setShowBulkResult(null)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.78rem' }}>Cerrar</button>
          </div>
          {showBulkResult.map(tk => (
            <div key={tk.token} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
              <span style={{ fontSize: '0.82rem', color: t.textStrong, flex: 1 }}>{tk.trabajador_nombre}</span>
              <button onClick={() => copyLink(tk.token)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid ${t.border}`, background: copied === tk.token ? '#10b981' : 'transparent', color: copied === tk.token ? '#fff' : t.textMuted, fontSize: '0.72rem', cursor: 'pointer' }}>
                {copied === tk.token ? <><Check size={10} /> Copiado</> : <><Copy size={10} /> Copiar</>}
              </button>
              {tk.contacto && tk.contacto.includes('@') && (
                <a href={mailtoLink(tk.token, tk.trabajador_nombre, tk.contacto)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: `1px solid ${t.border}`, color: t.textMuted, fontSize: '0.72rem', textDecoration: 'none' }}><Mail size={10} /> Email</a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Import section */}
      {showImport && <div style={{ padding: 16, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={descargarPlantilla} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.82rem', cursor: 'pointer' }}><Download size={14} /> Descargar plantilla</button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: t.accent, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
            <Upload size={14} /> Subir CSV
            <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) { setImportResult(null); procesarCSV(e.target.files[0]); } }} />
          </label>
        </div>
        {importResult && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: importResult.success ? (dark ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : (dark ? 'rgba(239,68,68,0.08)' : '#fef2f2'), fontSize: '0.82rem', color: importResult.success ? '#166534' : '#dc2626' }}>
            {importResult.success ? `✓ ${importResult.importados} trabajadores importados.` : `Error: ${importResult.error}`}
            {importResult.errores?.length > 0 && <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#f59e0b' }}>{importResult.errores.slice(0, 5).join(' · ')}</div>}
          </div>
        )}
      </div>}

      {/* Add worker form */}
      <div style={{ padding: 16, background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, marginBottom: 16 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong, marginBottom: 10 }}>Agregar trabajador</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
          <div><label style={labelStyle}>No. Empleado *</label><input style={inputStyle} placeholder="001" value={form.numEmpleado} onChange={e => setForm(p => ({ ...p, numEmpleado: e.target.value }))} /></div>
          <div><label style={labelStyle}>Nombre(s) *</label><input style={inputStyle} placeholder="Juan Carlos" value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} /></div>
          <div><label style={labelStyle}>Ap. Paterno *</label><input style={inputStyle} placeholder="Pérez" value={form.apellidoPaterno} onChange={e => setForm(p => ({ ...p, apellidoPaterno: e.target.value }))} /></div>
          <div><label style={labelStyle}>Ap. Materno</label><input style={inputStyle} placeholder="López" value={form.apellidoMaterno} onChange={e => setForm(p => ({ ...p, apellidoMaterno: e.target.value }))} /></div>
          <div><label style={labelStyle}>Método invitación</label><select style={{ ...inputStyle, width: '100%' }} value={form.metodoInvitacion} onChange={e => setForm(p => ({ ...p, metodoInvitacion: e.target.value }))}><option value="">Seleccionar</option><option>Correo electrónico</option><option>WhatsApp</option><option>Presencial</option></select></div>
          <div><label style={labelStyle}>Dato de contacto</label><input style={inputStyle} placeholder="ejemplo@empresa.com" value={form.contacto} onChange={e => setForm(p => ({ ...p, contacto: e.target.value }))} /></div>
        </div>
        <button onClick={agregarTrabajador} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: t.accent, color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', opacity: (form.nombre.trim() && form.apellidoPaterno.trim() && form.numEmpleado.trim()) ? 1 : 0.4 }}><Plus size={16} /> Agregar trabajador</button>
      </div>

      {/* Upgrade modal */}
      <UpgradeModal error={limitError} onClose={() => setLimitError(null)} />

      {/* Muestreo GIII selector (centros 51+) */}
      {obligaciones.nivel >= 3 && nMuestra && trabajadores.length > 0 && (
        <div style={{ padding: 16, background: dark ? 'rgba(99,102,241,0.06)' : '#f0f4ff', border: `1px solid ${dark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`, borderRadius: 12, marginBottom: 16 }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: t.textStrong, marginBottom: 8 }}>Estrategia de aplicación — Guía III (Numeral 7.1b)</div>
          <p style={{ fontSize: '0.78rem', color: t.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
            Para centros con más de 50 trabajadores, la Guía III puede aplicarse a todos o a una muestra representativa.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={() => setEstrategiaMuestreo('todos')} style={{
              padding: '10px 18px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              border: estrategiaMuestreo === 'todos' ? `2px solid ${t.accent}` : `1px solid ${t.border}`,
              background: estrategiaMuestreo === 'todos' ? (dark ? 'rgba(99,102,241,0.15)' : '#eef2ff') : 'transparent',
              color: estrategiaMuestreo === 'todos' ? t.textLink : t.textMuted,
            }}>
              Opción A: Todos los trabajadores ({trabajadores.length})
            </button>
            <button onClick={() => setEstrategiaMuestreo('muestra')} style={{
              padding: '10px 18px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              border: estrategiaMuestreo === 'muestra' ? `2px solid ${t.accent}` : `1px solid ${t.border}`,
              background: estrategiaMuestreo === 'muestra' ? (dark ? 'rgba(99,102,241,0.15)' : '#eef2ff') : 'transparent',
              color: estrategiaMuestreo === 'muestra' ? t.textLink : t.textMuted,
            }}>
              Opción B: Muestra representativa ({nMuestra} de {centro.numTrabajadores})
            </button>
            <button onClick={() => {
              if (estrategiaMuestreo === 'muestra') {
                // Select random workers for GIII
                const conFicha = trabajadores.filter(tw => !!fichas[tw.id]);
                const pool = conFicha.length > 0 ? conFicha : trabajadores;
                const shuffled = [...pool].sort(() => Math.random() - 0.5);
                const n = Math.min(nMuestra, shuffled.length);
                const seleccionados = shuffled.slice(0, n).map(tw => tw.id);
                dispatch({ type: ACTION_TYPES.SET_MUESTRA, payload: { ...state.muestraSeleccionada, [centro.id]: seleccionados } });
                alert(`✓ Muestra confirmada: ${n} trabajadores seleccionados aleatoriamente para Guía III.`);
              } else {
                dispatch({ type: ACTION_TYPES.SET_MUESTRA, payload: { ...state.muestraSeleccionada, [centro.id]: null } });
                alert('✓ Guía III se aplicará a todos los trabajadores.');
              }
            }} style={{
              padding: '10px 20px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
              border: 'none', background: t.accent, color: '#fff', marginLeft: 'auto',
            }}>
              ✓ Confirmar estrategia
            </button>
          </div>
          {estrategiaMuestreo === 'muestra' && (
            <div style={{ marginTop: 10, fontSize: '0.72rem', color: t.textFaint, lineHeight: 1.5 }}>
              Fórmula: n = 0.9604N / (0.0025(N-1) + 0.9604) = <strong>{nMuestra}</strong> trabajadores.
              Al confirmar, se seleccionarán {nMuestra} trabajadores de forma aleatoria para la Guía III.
            </div>
          )}
          {state.muestraSeleccionada[centro.id] && Array.isArray(state.muestraSeleccionada[centro.id]) && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(16,185,129,0.08)', borderRadius: 8, fontSize: '0.78rem', color: '#10b981', fontWeight: 600 }}>
              ✓ Muestra activa: {state.muestraSeleccionada[centro.id].length} trabajadores seleccionados
            </div>
          )}
        </div>
      )}

      {trabajadores.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 60, color: t.textFaint, textAlign: 'center' }}><Users size={32} /><p>No hay trabajadores registrados aún.</p></div>
      ) : <>
        {/* Search and filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ ...inputStyle, flex: 1, minWidth: 200, maxWidth: 350 }} placeholder="🔍 Buscar por nombre, No., área, puesto..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          <select style={{ ...inputStyle, width: 'auto', minWidth: 140 }} value={filtroEstatus} onChange={e => setFiltroEstatus(e.target.value)}>
            <option value="todos">Todos ({trabajadores.length})</option>
            <option value="completos">Completos</option>
            <option value="pendientes">Pendientes</option>
            <option value="sin_link">Sin link</option>
            <option value="con_link">Con link</option>
          </select>
          <span style={{ fontSize: '0.82rem', color: t.textMuted }}>
            {trabajadoresFiltrados.length === trabajadores.length ? `${trabajadores.length} trabajador${trabajadores.length !== 1 ? 'es' : ''}` : `${trabajadoresFiltrados.length} de ${trabajadores.length}`}
          </span>
        </div>
        <div style={{ borderRadius: 14, border: `1px solid ${t.border}`, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
              <thead><tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb' }}>
                <th style={{ ...thStyle, width: 60 }}>#</th><th style={thStyle}>Nombre</th><th style={thStyle}>Área</th><th style={thStyle}>Puesto</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 50 }}>Sexo</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Consent.</th><th style={{ ...thStyle, textAlign: 'center' }}>Ficha</th><th style={{ ...thStyle, textAlign: 'center' }}>G-I</th>
                {obligaciones.guias.includes("II") && <th style={{ ...thStyle, textAlign: 'center' }}>G-II</th>}
                {obligaciones.nivel >= 3 && <th style={{ ...thStyle, textAlign: 'center' }}>G-III</th>}
                
                <th style={{ ...thStyle, textAlign: 'center', width: 130 }}>Acción</th>
              </tr></thead>
              <tbody>{trabajadoresFiltrados.length === 0 ? (
                <tr><td colSpan={colSpan} style={{ padding: 30, textAlign: 'center', color: t.textFaint, fontSize: '0.85rem' }}>No se encontraron trabajadores.</td></tr>
              ) : trabajadoresFiltrados.map((trab, idx) => {
                const f = fichas[trab.id]; const consentOk = !!consentimientos[trab.id]; const fichaOk = !!f;
                const g1 = !!cuestionarios[`${trab.id}_guiaI`]; const g2 = !!cuestionarios[`${trab.id}_guiaII`]; const g3 = !!cuestionarios[`${trab.id}_guiaIII`];
                const isExp = expanded?.trabajadorId === trab.id;
                const rowBg = idx % 2 === 0 ? 'transparent' : (dark ? 'rgba(255,255,255,0.015)' : '#fafafa');
                return <Fragment key={trab.id}>
                  <tr style={{ background: isExp ? (dark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)') : rowBg, borderBottom: isExp ? 'none' : `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'}`, cursor: 'pointer' }}
                    onClick={() => setExpanded(isExp ? null : { trabajadorId: trab.id, tab: g1 ? 'I' : g2 ? 'II' : g3 ? 'III' : fichaOk ? 'ficha' : consentOk ? 'consent' : null })}>
                    <td style={{ padding: '10px 12px', fontSize: '0.78rem', fontWeight: 600, color: t.textLink }}>{trab.numEmpleado}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: t.textStrong }}>{trab.nombreCompleto || `${trab.nombre} ${trab.apellidos || ''}`}</div>
                      {trab.contacto && <div style={{ fontSize: '0.68rem', color: t.textFaint, marginTop: 1 }}>{trab.contacto}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '0.8rem', color: f ? t.textStrong : t.textFaint }}>{f?.area || f?.departamento || trab.area || '—'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '0.8rem', color: f ? t.textStrong : t.textFaint }}>{f?.puesto || trab.puesto || '—'}</td>
                    <td style={{ padding: '10px 12px', fontSize: '0.78rem', color: t.textMuted, textAlign: 'center' }}>{f ? (f.sexo === 'Mujer' || f.sexo === 'Femenino' ? 'F' : f.sexo === 'Hombre' || f.sexo === 'Masculino' ? 'M' : '—') : '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><Dot ok={consentOk} tab="consent" tid={trab.id} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><Dot ok={fichaOk} tab="ficha" tid={trab.id} /></td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}><Dot ok={g1} tab="I" tid={trab.id} /></td>
                    {obligaciones.guias.includes("II") && <td style={{ padding: '10px 12px', textAlign: 'center' }}><Dot ok={g2} tab="II" tid={trab.id} /></td>}
                    {obligaciones.nivel >= 3 && <td style={{ padding: '10px 12px', textAlign: 'center' }}><Dot ok={g3} tab="III" tid={trab.id} /></td>}
                    
                    <td style={{ padding: '10px 8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                        <PortalAction trab={trab} />
                        <button onClick={() => startEdit(trab)} title="Editar" style={{ padding: '4px 6px', borderRadius: 5, border: 'none', background: 'transparent', color: t.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }}><Pencil size={12} /></button>
                        <button onClick={() => handleDelete(trab)} disabled={deleting === trab.id} title="Eliminar" style={{ padding: '4px 6px', borderRadius: 5, border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', opacity: deleting === trab.id ? 0.3 : 0.6 }}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                  {isExp && <tr><td colSpan={colSpan} style={{ padding: 0, borderBottom: `1px solid ${t.border}` }}><ExpandedContent trab={trab} /></td></tr>}
                </Fragment>;
              })}</tbody>
            </table>
          </div>
        </div>
      </>}

      {/* Edit modal */}
      {editingTrab && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setEditingTrab(null)}>
          <div style={{ background: t.bgCard || '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: t.textStrong, margin: 0 }}>Editar trabajador</h3>
              <button onClick={() => setEditingTrab(null)} style={{ background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={labelStyle}>No. Empleado</label><input style={inputStyle} value={editForm.numEmpleado} onChange={e => setEditForm(p => ({ ...p, numEmpleado: e.target.value }))} /></div>
                <div><label style={labelStyle}>Nombre(s)</label><input style={inputStyle} value={editForm.nombre} onChange={e => setEditForm(p => ({ ...p, nombre: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={labelStyle}>Ap. Paterno</label><input style={inputStyle} value={editForm.apellidoPaterno} onChange={e => setEditForm(p => ({ ...p, apellidoPaterno: e.target.value }))} /></div>
                <div><label style={labelStyle}>Ap. Materno</label><input style={inputStyle} value={editForm.apellidoMaterno} onChange={e => setEditForm(p => ({ ...p, apellidoMaterno: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label style={labelStyle}>Método invitación</label><select style={{ ...inputStyle, width: '100%' }} value={editForm.metodoInvitacion} onChange={e => setEditForm(p => ({ ...p, metodoInvitacion: e.target.value }))}><option value="">Seleccionar</option><option>Correo electrónico</option><option>WhatsApp</option><option>Presencial</option></select></div>
                <div><label style={labelStyle}>Contacto</label><input style={inputStyle} value={editForm.contacto} onChange={e => setEditForm(p => ({ ...p, contacto: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setEditingTrab(null)} style={{ padding: '10px 18px', borderRadius: 8, border: `1px solid ${t.border}`, background: 'transparent', color: t.textMuted, fontSize: '0.85rem', cursor: 'pointer' }}>Cancelar</button>
                <button onClick={saveEdit} style={{ padding: '10px 18px', borderRadius: 8, border: 'none', background: t.accent, color: '#fff', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Guardar cambios</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
